import { FC, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { Controller } from 'react-hook-form';
import { Listbox } from '@headlessui/react';
import { ReactComponent as TriangleIcon } from 'assets/icons/triangle.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/drop-down.svg';
import { EXAM_PLACEMENT } from 'utils/constants';
import BeforeAfterImage from 'assets/images/before-after.gif';
import BeforeImage from 'assets/images/before.png';
import AfterImage from 'assets/images/after.png';
import FirstPartSameImage from 'assets/images/first-part-same.png';
import LastPartSameImage from 'assets/images/last-part-same.png';
import FirstPartDiffImage from 'assets/images/first-part-diff.png';
import LastPartDiffImage from 'assets/images/last-part-diff.png';

interface ExamPlacementSectionProps {
  control: any;
}

const EXAM_PLACEMENT_OPTIONS = [
  { label: 'Before/after hygiene appt (Different Chair)', value: null },
  {
    label: 'Before hygiene appt (Different Chair)',
    value: EXAM_PLACEMENT.HEAD,
  },
  { label: 'After hygiene appt (Different Chair)', value: EXAM_PLACEMENT.TAIL },
  {
    label: 'Before hygiene appt (Same chair)',
    value: EXAM_PLACEMENT.HEAD_SAME_CHAIR,
  },
  {
    label: 'After hygiene appt (Same chair)',
    value: EXAM_PLACEMENT.TAIL_SAME_CHAIR,
  },
  {
    label: 'During first part of hygiene appt (Different chair)',
    value: EXAM_PLACEMENT.DURING_FIRST_PART_DIFF,
  },
  {
    label: 'During last part of hygiene appt (Different chair)',
    value: EXAM_PLACEMENT.DURING_LAST_PART_DIFF,
  },
];

const getImageSource = (value: string) => {
  if (value === null) {
    return BeforeAfterImage;
  }
  if (value === EXAM_PLACEMENT.HEAD) {
    return BeforeImage;
  }
  if (value === EXAM_PLACEMENT.TAIL) {
    return AfterImage;
  }
  if (value === EXAM_PLACEMENT.HEAD_SAME_CHAIR) {
    return FirstPartSameImage;
  }
  if (value === EXAM_PLACEMENT.TAIL_SAME_CHAIR) {
    return LastPartSameImage;
  }
  if (value === EXAM_PLACEMENT.DURING_FIRST_PART_DIFF) {
    return FirstPartDiffImage;
  }
  if (value === EXAM_PLACEMENT.DURING_LAST_PART_DIFF) {
    return LastPartDiffImage;
  }
};

const ExamPlacementSection: FC<ExamPlacementSectionProps> = ({ control }) => {
  const [isExapanded, setIsExapanded] = useState(true);

  return (
    <div className="flex flex-col gap-y-1.4">
      <button
        className="flex items-center gap-x-0.8"
        onClick={() => setIsExapanded((isExapanded) => !isExapanded)}
      >
        <TriangleIcon
          className={`${
            isExapanded ? '' : '-rotate-90'
          } transition-transform w-1.6 h-1.6`}
        />
        <span className="font-bold text-16 leading-[2.2rem] text-darkest-grey">
          Exam Placement
        </span>
        <div data-for="exam-placement-info" data-tip="">
          <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
        </div>
        <ReactTooltip
          id="exam-placement-info"
          effect="solid"
          place="bottom"
          className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
        >
          <span className="text-11 text-darkest-grey flex items-center font-normal text-left">
            This setting will apply to all exam and cleaning services with
            activated split scheduling.
          </span>
        </ReactTooltip>
      </button>
      {isExapanded && (
        <Controller
          control={control}
          name={'examPlacement'}
          render={({ field }) => {
            return (
              <>
                <Listbox {...field}>
                  {({ open }) => {
                    return (
                      <div className="relative text-14 leading-[2.1rem]">
                        <Listbox.Button
                          className={`relative w-full h-[4rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active outline-none ${
                            open ? 'border-magenta shadow-input-active' : ''
                          }`}
                        >
                          <span className="block pr-3 text-darkest-grey">
                            {
                              EXAM_PLACEMENT_OPTIONS.find(
                                (item) => item.value === field.value
                              )!.label
                            }
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.6">
                            <DropdownIcon
                              className={`${
                                open ? 'fill-[#666]' : 'fill-[#ccc]'
                              } transition-[fill] shrink-0`}
                            />
                          </span>
                        </Listbox.Button>
                        <Listbox.Options className="absolute mt-0.8 w-full rounded-[0.8rem] bg-white py-1.5 text-base shadow-primary flex flex-col gap-y-1.5 z-20">
                          {EXAM_PLACEMENT_OPTIONS.map(({ label, value }) => (
                            <Listbox.Option
                              key={value}
                              className="relative cursor-default select-none pl-1.6"
                              value={value}
                            >
                              {({ selected }) => (
                                <span
                                  className={`block truncate cursor-pointer hover:text-magenta ${
                                    selected ? 'text-magenta' : ''
                                  }`}
                                >
                                  {label}
                                </span>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </div>
                    );
                  }}
                </Listbox>
                <div className="w-full h-[30rem] flex justify-center">
                  {field.value !== null ? (
                    <img
                      className="w-full h-full"
                      src={getImageSource(field.value)}
                      alt="Exam Placement"
                    />
                  ) : (
                    <img
                      className="w-full h-full overflow-hidden border border-lightest-grey rounded-[1rem] shadow-primary scale-[0.97]"
                      src={getImageSource(field.value)}
                      alt="Exam Placement"
                    />
                  )}{' '}
                </div>
              </>
            );
          }}
        />
      )}
    </div>
  );
};

export default ExamPlacementSection;
